import React, { useEffect, useState, useContext } from "react";
import {
	TextField,
	InputAdornment,
	Checkbox,
	IconButton,
	Alert,
	Box,
	List,
	ListItem,
	ListItemText,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Button,
	CircularProgress,
	Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { obtenerDatos, agregarDato, auditPost } from "../../api/config";
import { ModalFecha } from "./ModalFechaMas";
import Swal from "sweetalert2";
import { GlobalContext } from "../../GlobalContext";
import { useTranslation } from "react-i18next";

const getStatusColor = (status) => {
	switch (status) {
		case "ejecutando":
			return "grey";
		case "infactible":
			return "red";
		case "finalizado":
			return "green";
		default:
			return "grey";
	}
};
const ExecuteModule = () => {
	const { t } = useTranslation();
	const [searchTermAvailable, setSearchTermAvailable] = useState("");
	const [datos, setDatos] = useState([]);
	const [selectedItems, setSelectedItems] = useState([]);
	const [selectedIds, setSelectedIds] = useState([]);
	const [showAlert, setShowAlert] = useState(false);
	const [open, setOpen] = useState(false);
	const [showSimulations, setShowSimulations] = useState(true);
	const [datosSimulacion, setDatosSimulacion] = useState({});
	const [loading, setLoading] = useState(true);
	const [loadingSim, setLoadingSim] = useState(true);
	const [error, setError] = useState(false);
	const { getModuleId, site_id } = useContext(GlobalContext);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			setError(false);
			try {
				const data = await obtenerDatos(`/workflows/get_by_user?site_id=${site_id}`);
				setDatosSimulacion(data);
			} catch (error) {
				console.error("Error al obtener datos de simulación:", error);
				setError(true);
			} finally {
				setLoading(false);
			}
		};

		fetchData();

		const intervalId = setInterval(fetchData, 30000);

		return () => clearInterval(intervalId);
	}, [site_id]);

	const renderStatusIcon = (status) => {
		return status === "ejecutando" ? (
			<CircularProgress size={20} color="inherit" />
		) : (
			<CheckCircleIcon color="success" />
		);
	};

	useEffect(() => {
		const getData = async () => {
			setLoadingSim(true);
			setError(false);
			try {
				const response = await obtenerDatos(`user_site/get_sites_by_user?site_id=${site_id}`);
				setDatos(response);
			} catch (error) {
				console.error("Error al obtener sitios:", error);
				setError(true);
			} finally {
				setLoadingSim(false);
			}
		};
		getData();
	}, [site_id]);
	const handleToggle = (key) => {
		const item = { key: key, id: datos[key].id };

		if (selectedItems.some((selected) => selected.key === key)) {
			setSelectedItems((prevSelected) => {
				const updatedSelected = prevSelected.filter((selected) => selected.key !== key);
				return updatedSelected;
			});
		} else if (selectedItems.length < 5) {
			setSelectedItems((prevSelected) => {
				const updatedSelected = [...prevSelected, item];

				return updatedSelected;
			});
		}
	};

	const filterDatos = (datos, searchTerm) => {
		if (searchTerm) {
			return (
				Object.keys(datos || {})
					.filter(
						(key) =>
							key.toLowerCase().includes(searchTerm) ||
							(datos[key].full_site_name && datos[key].full_site_name.toLowerCase().includes(searchTerm))
					)
					// eslint-disable-next-line no-sequences
					.reduce((res, key) => ((res[key] = datos[key]), res), {})
			);
		} else {
			return datos;
		}
	};

	const handleSearchChange = (e) => {
		setSearchTermAvailable(e.target.value);
	};

	const clearSelection = () => {
		setSelectedItems([]);
		setSearchTermAvailable("");
	};

	const filteredDatos = filterDatos(datos, searchTermAvailable.toLowerCase());

	const handleContinue = () => {
		if (selectedItems.length === 0 || selectedItems.length === 1) {
			setShowAlert(true);
			return;
		} else {
			setOpen(true);
			setShowAlert(false);
			const selectedIds = selectedItems.map((item) => item.id);

			setSelectedIds(selectedIds);
		}
	};
	const handleClose = () => setOpen(false);
	// ——— Ejecutar simulaciones MASIVAS ————————————————————————————
	const handleExecuteSimulation = (
		fechaInicio,
		fechaFin,
		checked,
		formValues,
		emailNotifications,
		selectedForecast
	) => {
		if (!selectedIds || selectedIds.length === 0) {
			Swal.fire({
				title: t("aux.messages.alert.error"),
				text: t("aux.messages.alert.text_alert.no_sites"),
				icon: "error",
			});
			return;
		}

		const datosDefaultEnpoint = {
			sites_id: selectedIds,
			name: formValues.name,
			description: formValues.description,
			start: fechaInicio,
			end: fechaFin,
			forecast_origin: checked,
			send_mail: emailNotifications,
			forecast_id: selectedForecast,
		};

		Swal.fire({
			title: t("aux.messages.sure"),
			text: t("aux.messages.not_revert"),
			icon: "question",
			showCancelButton: true,
			confirmButtonColor: "#3085d6",
			cancelButtonColor: "#d33",
			confirmButtonText: t("aux.buttons.yes"),
		}).then((result) => {
			if (result.isConfirmed) {
				Swal.fire({
					title: t("aux.messages.alert.info"),
					text: t("aux.messages.alert.text_alert.wait_sim"),
					icon: "info",
					allowOutsideClick: false,
					showConfirmButton: false,
					onBeforeOpen: () => {
						Swal.showLoading();
					},
				});

				agregarDato("/integration/execute-parallel-launch", datosDefaultEnpoint)
					.then(() => {
						const moduleId = getModuleId("Sim. Masivas", "Ejecutar simulaciones masivas");
						auditPost(moduleId, site_id);

						Swal.fire({
							title: t("aux.messages.alert.success"),
							text: t("aux.messages.alert.text_alert.success_sim"),
							icon: "success",
						});
						setShowSimulations(true);
						clearSelection();
						setSelectedIds([]);
					})
					.catch((error) => {
						console.error("Error al ejecutar simulación masiva:", error);
						Swal.fire({
							title: t("aux.messages.alert.error"),
							text: t("aux.messages.alert.text_alert.error_sim"),
							icon: "error",
						});
					});
			}
		});
	};

	return (
		<>
			<Box
				sx={{
					width: "100%",
					p: 2,
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Typography variant="h5" gutterBottom>
					{t("sim_masive.title_module")}
				</Typography>
				<Box
					sx={{
						p: 2,
						width: "75%",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Box
							sx={{
								width: "100%",
								height: showSimulations ? "30vh" : "50vh",
								px: 2,
								py: 1,
								border: 1,
								borderColor: "secondary.main",
								borderRadius: 1,
								justifyContent: "space-between",
								overflowY: "auto",
							}}
						>
							<List>
								{selectedItems.length > 0 ? (
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											flexDirection: "column",
											backgroundColor: "background.paper",
										}}
									>
										<TextField
											sx={{
												width: "100%",
												borderColor: "secondary.main",
												mb: 1,
											}}
											label={t("aux.search")}
											size="small"
											value={searchTermAvailable}
											onChange={handleSearchChange}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<SearchIcon />
													</InputAdornment>
												),
											}}
											type="search"
										/>
										<Box
											sx={{
												width: "100%",
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												border: 1,
												borderColor: "secondary.main",
												borderRadius: 1,
												mb: 1,
												px: 2,
												py: 1,
												backgroundColor: "background.paper",
											}}
										>
											<Typography variant="subtitle1">
												{selectedItems.length} {t("aux.items_selected")}
											</Typography>
											<IconButton onClick={clearSelection}>
												<CloseIcon />
											</IconButton>
										</Box>
									</Box>
								) : (
									<TextField
										sx={{
											width: "100%",
											borderColor: "secondary.main",
											mb: 1,
										}}
										label={t("aux.search")}
										size="small"
										value={searchTermAvailable}
										onChange={handleSearchChange}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<SearchIcon />
												</InputAdornment>
											),
										}}
										type="search"
									/>
								)}

								<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
									{loadingSim && <CircularProgress />}
								</Box>
								{Object.keys(filteredDatos).map((key) => (
									<ListItem
										sx={{
											width: "100%",
											height: "8vh",
											border: 1,
											borderColor: "secondary.main",
											borderRadius: 1,
											my: 1,
											backgroundColor: selectedItems.some((selected) => selected.key === key)
												? "primary.light"
												: "background.paper",
										}}
										key={key}
									>
										<Checkbox
											checked={selectedItems.some((selected) => selected.key === key)}
											onChange={() => handleToggle(key)}
											color="primary"
											sx={{ mr: 1 }}
											disabled={
												!selectedItems.some((selected) => selected.key === key) && selectedItems.length >= 10
											}
										/>
										<ListItemText
											primary={`${filteredDatos[key].full_site_name}`}
											sx={{ textAlign: "center" }}
										/>
									</ListItem>
								))}
							</List>
						</Box>
					</Box>
					{showAlert && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{t("aux.messages.alert.sim_error.list")}
						</Alert>
					)}
					<Box
						sx={{
							mt: 2,
							width: "100%",
							display: "flex",
							justifyContent: "end",
						}}
					>
						<Button
							color="primary"
							variant="contained"
							sx={{
								width: "40%",
							}}
							onClick={handleContinue}
						>
							{t("aux.buttons.continue")}
						</Button>
					</Box>
				</Box>

				{showSimulations && (
					<>
						<Typography variant="h5" gutterBottom sx={{ mt: 3, justifyContent: "center" }}>
							{t("sim_masive.title_on_curse")}
						</Typography>
						<Typography variant="subtitle2" color="primary">
							{t("sim_masive.sub_title")}
						</Typography>
						<Box
							sx={{
								p: 2,
								width: "75%",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box
								sx={{
									mt: 2,
									width: "100%",
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<Box
									sx={{
										width: "100%",
										height: "30vh",
										px: 2,
										py: 1,
										border: 1,
										borderColor: "secondary.main",
										borderRadius: 1,
										justifyContent: "space-between",
										overflowY: "auto",
									}}
								>
									<List>
										<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
											{loading && <CircularProgress />}
										</Box>
										{error && (
											<Alert severity="error">
												{t("aux.messages.alert.sim_error.error_on_curse")} {error.message}
											</Alert>
										)}
										{!loading && !error ? (
											datosSimulacion && datosSimulacion.status_code === 404 ? (
												<Typography variant="body1">{t("sim_masive.no_simulations")}</Typography>
											) : Object.keys(datosSimulacion || {}).length > 0 ? (
												Object.keys(datosSimulacion).map((simulationName, index) => {
													const simulationData = datosSimulacion[simulationName] || {};
													const hasRunning = Object.values(simulationData).includes("ejecutando");

													return (
														<Accordion key={index}>
															<AccordionSummary
																expandIcon={<ExpandMoreIcon />}
																aria-controls={`panel${index}-content`}
																id={`panel${index}-header`}
																sx={{ display: "flex", alignItems: "center" }}
															>
																<Typography sx={{ flexGrow: 1 }}>{simulationName}</Typography>
																{renderStatusIcon(hasRunning ? "ejecutando" : "Completado")}
															</AccordionSummary>
															<AccordionDetails>
																{Object.keys(simulationData).length > 0 ? (
																	<List>
																		{Object.entries(simulationData).map(([siteName, status], idx) => (
																			<ListItem key={idx} sx={{ display: "flex", alignItems: "center" }}>
																				<ListItemText primary={siteName} secondary={status} />
																				<Tooltip title={status} placement="top">
																					<FiberManualRecordIcon
																						sx={{
																							color: getStatusColor(status),
																							ml: 2,
																						}}
																					/>
																				</Tooltip>
																			</ListItem>
																		))}
																	</List>
																) : (
																	<Typography variant="subtitle1">{t("aux.no_data")}</Typography>
																)}
															</AccordionDetails>
														</Accordion>
													);
												})
											) : (
												<Typography variant="subtitle1">{t("aux.no_data")}</Typography>
											)
										) : null}
									</List>
								</Box>
							</Box>
						</Box>
					</>
				)}
			</Box>
			<ModalFecha open={open} onClose={handleClose} onExecuteSimulation={handleExecuteSimulation} />
		</>
	);
};

export { ExecuteModule };
